import type { LoadAppPayload } from '@cogo/iframe-protocol'

export const PREVIEW_REVIEW_STORAGE_KEY = 'cogo-studio:preview-review:v1'
export const PREVIEW_REVIEW_SNAPSHOT_VERSION = 1

export type PreviewShareRole = 'view' | 'comment' | 'suggest' | 'edit-preview' | 'owner'
export type PreviewShareVisibility = 'private' | 'team' | 'unlisted' | 'public'
export type ReviewItemKind = 'comment' | 'replaceText' | 'replaceImage' | 'altText' | 'promptSuggestion'
export type ReviewItemStatus = 'open' | 'resolved' | 'accepted' | 'rejected'
export type PreviewDraftRenderStatus = 'idle' | 'rendering' | 'ready' | 'failed'

export interface ReviewRollbackPatch {
  op: 'add' | 'remove' | 'replace'
  path: string
  value?: unknown
}

export interface PreviewTargetRef {
  pageId?: string
  blockId?: string
  nodeId?: string
  path?: string
  rect?: { x: number; y: number; width: number; height: number }
  label?: string
}

export type ReviewPatch =
  | {
      op: 'replaceText'
      targetRef: PreviewTargetRef
      previousText?: string
      nextText: string
    }
  | {
      op: 'replaceImage'
      targetRef: PreviewTargetRef
      previousAssetRef?: string
      nextAssetRef: string
      altText: string
    }
  | {
      op: 'setAltText'
      targetRef: PreviewTargetRef
      nextAltText: string
    }

export interface PreviewSnapshot {
  id: string
  projectId: string
  projectVersionId: string
  title: string
  appPayload?: LoadAppPayload
  sourcePreviewUrl?: string
  appSpecRef?: string
  designSystemRef?: string
  assetManifestRef?: string
  actionflowRef?: string
  renderPackRef?: string
  sourceTrace?: Record<string, unknown>
  evidence?: Record<string, unknown>
  createdBy: string
  createdAt: string
}

export interface PreviewShare {
  id: string
  previewId: string
  tokenHash: string
  role: PreviewShareRole
  visibility: PreviewShareVisibility
  expiresAt?: string
  revokedAt?: string
  createdBy: string
  createdAt: string
}

export interface ReviewItem {
  id: string
  previewId: string
  shareId?: string
  targetRef: PreviewTargetRef
  kind: ReviewItemKind
  body: string
  proposedPatch?: ReviewPatch
  status: ReviewItemStatus
  createdByDisplay?: string
  createdAt: string
  updatedAt?: string
}

export interface PreviewDraft {
  id: string
  previewId: string
  baseSnapshotId: string
  patches: ReviewPatch[]
  renderStatus: PreviewDraftRenderStatus
  createdByShareId?: string
  createdAt: string
  updatedAt: string
}

export interface AcceptedChangeSet {
  id: string
  projectId: string
  previewId: string
  reviewItemIds: string[]
  patches: ReviewPatch[]
  rollbackPatches?: ReviewRollbackPatch[]
  appliedVersionId: string
  rollbackVersionId: string
  acceptedBy: string
  acceptedAt: string
  rollbackAppliedVersionId?: string
  rolledBackBy?: string
  rolledBackAt?: string
}

export interface PreviewReviewSnapshot {
  version: typeof PREVIEW_REVIEW_SNAPSHOT_VERSION
  previews: PreviewSnapshot[]
  shares: PreviewShare[]
  reviewItems: ReviewItem[]
  drafts: PreviewDraft[]
  acceptedChangeSets: AcceptedChangeSet[]
}

export interface PreviewReviewStorage {
  load(): PreviewReviewSnapshot | null
  save(snapshot: PreviewReviewSnapshot): void
  clear?(): void
}

export interface PreviewReviewStoreOptions {
  clock?: () => Date
  idFactory?: (prefix: string) => string
  tokenFactory?: () => string
  storage?: PreviewReviewStorage | null
  origin?: string
}

export interface CreatePreviewSnapshotInput {
  projectId: string
  projectVersionId: string
  title: string
  appPayload?: LoadAppPayload
  sourcePreviewUrl?: string
  appSpecRef?: string
  designSystemRef?: string
  assetManifestRef?: string
  actionflowRef?: string
  renderPackRef?: string
  sourceTrace?: Record<string, unknown>
  evidence?: Record<string, unknown>
  createdBy?: string
}

export interface CreatePreviewShareInput {
  previewId: string
  role?: PreviewShareRole
  visibility?: PreviewShareVisibility
  expiresAt?: string
  createdBy?: string
  rawToken?: string
}

export interface UpdatePreviewShareInput {
  role?: PreviewShareRole
  visibility?: PreviewShareVisibility
  expiresAt?: string | null
}

export interface CreatePreviewShareOutput {
  share: PreviewShare
  rawToken: string
  shareUrl: string
  reviewUrl: string
}

export interface CreatePreviewDraftInput {
  previewId: string
  baseSnapshotId?: string
  patches: ReviewPatch[]
  createdByShareId?: string
  renderStatus?: PreviewDraftRenderStatus
}

export interface AcceptReviewItemsInput {
  projectId: string
  previewId: string
  reviewItemIds: string[]
  rollbackPatches?: ReviewRollbackPatch[]
  appliedVersionId: string
  rollbackVersionId: string
  acceptedBy: string
}

export interface RollbackAcceptedChangeSetInput {
  rollbackAppliedVersionId: string
  rolledBackBy: string
}

function defaultIdFactory(prefix: string): string {
  const random = globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
  return `${prefix}_${random.replaceAll('-', '').slice(0, 20)}`
}

function defaultTokenFactory(): string {
  const random = globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
  return `ps_${random.replaceAll('-', '')}`
}

function defaultOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin
  return 'https://cogo.xyz'
}

function iso(clock: () => Date): string {
  return clock().toISOString()
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function isSnapshot(value: unknown): value is PreviewReviewSnapshot {
  if (!isRecord(value)) return false
  return value.version === PREVIEW_REVIEW_SNAPSHOT_VERSION &&
    Array.isArray(value.previews) &&
    Array.isArray(value.shares) &&
    Array.isArray(value.reviewItems) &&
    Array.isArray(value.drafts) &&
    Array.isArray(value.acceptedChangeSets)
}

export function isPreviewReviewSnapshot(value: unknown): value is PreviewReviewSnapshot {
  return isSnapshot(value)
}

export function emptyPreviewReviewSnapshot(): PreviewReviewSnapshot {
  return {
    version: PREVIEW_REVIEW_SNAPSHOT_VERSION,
    previews: [],
    shares: [],
    reviewItems: [],
    drafts: [],
    acceptedChangeSets: [],
  }
}

export class LocalStoragePreviewReviewStorage implements PreviewReviewStorage {
  constructor(private readonly key = PREVIEW_REVIEW_STORAGE_KEY) {}

  load(): PreviewReviewSnapshot | null {
    try {
      if (typeof localStorage === 'undefined') return null
      const raw = localStorage.getItem(this.key)
      if (!raw) return null
      const parsed = JSON.parse(raw) as unknown
      return isSnapshot(parsed) ? parsed : null
    } catch {
      return null
    }
  }

  save(snapshot: PreviewReviewSnapshot): void {
    try {
      if (typeof localStorage === 'undefined') return
      localStorage.setItem(this.key, JSON.stringify(snapshot))
    } catch {
      // Local quota failures must not break Studio rendering.
    }
  }

  clear(): void {
    try {
      if (typeof localStorage === 'undefined') return
      localStorage.removeItem(this.key)
    } catch {
      // ignore
    }
  }
}

export function previewSnapshotUrl(origin: string, previewId: string): string {
  return `${origin.replace(/\/$/, '')}/p/${encodeURIComponent(previewId)}`
}

export function previewShareUrl(origin: string, previewId: string, token: string): string {
  const url = new URL(previewSnapshotUrl(origin, previewId))
  url.searchParams.set('share', token)
  return url.toString()
}

export function previewReviewUrl(origin: string, previewId: string, token: string): string {
  const url = new URL(`${previewSnapshotUrl(origin, previewId)}/review`)
  url.searchParams.set('share', token)
  return url.toString()
}

export function hashPreviewShareToken(token: string): string {
  let hash = 2166136261
  for (let i = 0; i < token.length; i += 1) {
    hash ^= token.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return `local-fnv1a:${(hash >>> 0).toString(16).padStart(8, '0')}`
}

export function canPreviewShare(role: PreviewShareRole, action: 'view' | 'comment' | 'suggest' | 'edit-preview' | 'owner'): boolean {
  const rank: Record<PreviewShareRole, number> = {
    view: 1,
    comment: 2,
    suggest: 3,
    'edit-preview': 4,
    owner: 5,
  }
  const required: Record<typeof action, number> = {
    view: 1,
    comment: 2,
    suggest: 3,
    'edit-preview': 4,
    owner: 5,
  }
  return rank[role] >= required[action]
}

export function isPreviewShareActive(share: PreviewShare, now = new Date()): boolean {
  if (share.revokedAt) return false
  if (!share.expiresAt) return true
  const expiresAt = Date.parse(share.expiresAt)
  return Number.isFinite(expiresAt) && expiresAt > now.getTime()
}

export function canUsePreviewShare(
  share: PreviewShare,
  action: 'view' | 'comment' | 'suggest' | 'edit-preview' | 'owner',
  now = new Date(),
): boolean {
  return isPreviewShareActive(share, now) && canPreviewShare(share.role, action)
}

export function validateReviewPatch(patch: ReviewPatch): string[] {
  const errors: string[] = []
  if (!patch.targetRef.nodeId && !patch.targetRef.blockId && !patch.targetRef.path) {
    errors.push('Review patch requires a stable nodeId, blockId, or path target.')
  }
  if (patch.op === 'replaceText' && patch.nextText.trim().length === 0) {
    errors.push('replaceText requires non-empty nextText.')
  }
  if (patch.op === 'replaceImage') {
    if (!patch.nextAssetRef.trim()) errors.push('replaceImage requires nextAssetRef.')
    if (!patch.altText.trim()) errors.push('replaceImage requires altText.')
  }
  if (patch.op === 'setAltText' && !patch.nextAltText.trim()) {
    errors.push('setAltText requires nextAltText.')
  }
  return errors
}

export class PreviewReviewStore {
  private readonly clock: () => Date
  private readonly idFactory: (prefix: string) => string
  private readonly tokenFactory: () => string
  private readonly storage: PreviewReviewStorage | null
  private readonly origin: string
  private readonly previews = new Map<string, PreviewSnapshot>()
  private readonly shares = new Map<string, PreviewShare>()
  private readonly rawTokenToShareId = new Map<string, string>()
  private readonly reviewItems = new Map<string, ReviewItem>()
  private readonly drafts = new Map<string, PreviewDraft>()
  private readonly acceptedChangeSets = new Map<string, AcceptedChangeSet>()

  constructor(options: PreviewReviewStoreOptions = {}) {
    this.clock = options.clock ?? (() => new Date())
    this.idFactory = options.idFactory ?? defaultIdFactory
    this.tokenFactory = options.tokenFactory ?? defaultTokenFactory
    this.storage = options.storage === undefined ? new LocalStoragePreviewReviewStorage() : options.storage
    this.origin = options.origin ?? defaultOrigin()
    this.hydrate()
  }

  createPreviewSnapshot(input: CreatePreviewSnapshotInput): PreviewSnapshot {
    const createdAt = iso(this.clock)
    const preview: PreviewSnapshot = {
      id: this.idFactory('preview'),
      projectId: input.projectId,
      projectVersionId: input.projectVersionId,
      title: input.title,
      appPayload: input.appPayload,
      sourcePreviewUrl: input.sourcePreviewUrl,
      appSpecRef: input.appSpecRef,
      designSystemRef: input.designSystemRef,
      assetManifestRef: input.assetManifestRef,
      actionflowRef: input.actionflowRef,
      renderPackRef: input.renderPackRef,
      sourceTrace: input.sourceTrace,
      evidence: input.evidence,
      createdBy: input.createdBy ?? 'studio',
      createdAt,
    }
    this.previews.set(preview.id, preview)
    this.persist()
    return preview
  }

  createPreviewShare(input: CreatePreviewShareInput): CreatePreviewShareOutput {
    const preview = this.previews.get(input.previewId)
    if (!preview) throw new Error(`Unknown PreviewSnapshot: ${input.previewId}`)
    const rawToken = input.rawToken?.trim() || this.tokenFactory()
    const tokenHash = hashPreviewShareToken(rawToken)
    const share: PreviewShare = {
      id: this.idFactory('share'),
      previewId: input.previewId,
      tokenHash,
      role: input.role ?? 'view',
      visibility: input.visibility ?? 'unlisted',
      expiresAt: input.expiresAt,
      createdBy: input.createdBy ?? preview.createdBy,
      createdAt: iso(this.clock),
    }
    this.shares.set(share.id, share)
    this.rawTokenToShareId.set(rawToken, share.id)
    this.persist()
    return {
      share,
      rawToken,
      shareUrl: previewShareUrl(this.origin, input.previewId, rawToken),
      reviewUrl: previewReviewUrl(this.origin, input.previewId, rawToken),
    }
  }

  getPreviewSnapshot(previewId: string): PreviewSnapshot | undefined {
    return this.previews.get(previewId)
  }

  getPreviewShares(previewId: string): PreviewShare[] {
    return Array.from(this.shares.values()).filter((share) => share.previewId === previewId)
  }

  resolveShareByToken(rawToken: string): PreviewShare | undefined {
    const tokenHash = hashPreviewShareToken(rawToken)
    return Array.from(this.shares.values()).find((share) => share.tokenHash === tokenHash)
  }

  updatePreviewShare(shareId: string, input: UpdatePreviewShareInput): PreviewShare {
    const share = this.shares.get(shareId)
    if (!share) throw new Error(`Unknown PreviewShare: ${shareId}`)
    if (input.expiresAt !== undefined && input.expiresAt !== null) {
      const expiresAt = Date.parse(input.expiresAt)
      if (!Number.isFinite(expiresAt)) throw new Error('PreviewShare expiresAt must be a valid ISO timestamp.')
    }
    const next: PreviewShare = {
      ...share,
      ...(input.role ? { role: input.role } : {}),
      ...(input.visibility ? { visibility: input.visibility } : {}),
      ...(input.expiresAt === null ? { expiresAt: undefined } : input.expiresAt !== undefined ? { expiresAt: input.expiresAt } : {}),
    }
    this.shares.set(shareId, next)
    this.persist()
    return next
  }

  revokePreviewShare(shareId: string): PreviewShare {
    const share = this.shares.get(shareId)
    if (!share) throw new Error(`Unknown PreviewShare: ${shareId}`)
    if (share.revokedAt) return share
    const next: PreviewShare = {
      ...share,
      revokedAt: iso(this.clock),
    }
    this.shares.set(shareId, next)
    this.persist()
    return next
  }

  createReviewItem(input: Omit<ReviewItem, 'id' | 'createdAt' | 'status'> & { status?: ReviewItemStatus }): ReviewItem {
    const preview = this.previews.get(input.previewId)
    if (!preview) throw new Error(`Unknown PreviewSnapshot: ${input.previewId}`)
    if (input.proposedPatch) {
      const errors = validateReviewPatch(input.proposedPatch)
      if (errors.length > 0) throw new Error(errors.join(' '))
    }
    const item: ReviewItem = {
      ...input,
      id: this.idFactory('review'),
      status: input.status ?? 'open',
      createdAt: iso(this.clock),
    }
    this.reviewItems.set(item.id, item)
    this.persist()
    return item
  }

  createPreviewDraft(input: CreatePreviewDraftInput): PreviewDraft {
    const preview = this.previews.get(input.previewId)
    if (!preview) throw new Error(`Unknown PreviewSnapshot: ${input.previewId}`)
    const patchErrors = input.patches.flatMap(validateReviewPatch)
    if (patchErrors.length > 0) throw new Error(patchErrors.join(' '))
    const createdAt = iso(this.clock)
    const draft: PreviewDraft = {
      id: this.idFactory('draft'),
      previewId: input.previewId,
      baseSnapshotId: input.baseSnapshotId ?? input.previewId,
      patches: input.patches,
      renderStatus: input.renderStatus ?? 'ready',
      createdByShareId: input.createdByShareId,
      createdAt,
      updatedAt: createdAt,
    }
    this.drafts.set(draft.id, draft)
    this.persist()
    return draft
  }

  resolveReviewItem(reviewItemId: string): ReviewItem {
    return this.updateReviewItemStatus(reviewItemId, 'resolved')
  }

  rejectReviewItem(reviewItemId: string): ReviewItem {
    return this.updateReviewItemStatus(reviewItemId, 'rejected')
  }

  acceptReviewItems(input: AcceptReviewItemsInput): AcceptedChangeSet {
    const preview = this.previews.get(input.previewId)
    if (!preview) throw new Error(`Unknown PreviewSnapshot: ${input.previewId}`)
    const items = input.reviewItemIds.map((id) => {
      const item = this.reviewItems.get(id)
      if (!item) throw new Error(`Unknown ReviewItem: ${id}`)
      if (item.previewId !== input.previewId) throw new Error(`ReviewItem ${id} does not belong to PreviewSnapshot ${input.previewId}.`)
      return item
    })
    const patches = items.flatMap((item) => item.proposedPatch ? [item.proposedPatch] : [])
    if (patches.length === 0) throw new Error('Accepting review items requires at least one proposed patch.')
    const patchErrors = patches.flatMap(validateReviewPatch)
    if (patchErrors.length > 0) throw new Error(patchErrors.join(' '))
    const changeSet: AcceptedChangeSet = {
      id: this.idFactory('changeset'),
      projectId: input.projectId,
      previewId: input.previewId,
      reviewItemIds: input.reviewItemIds,
      patches,
      rollbackPatches: input.rollbackPatches,
      appliedVersionId: input.appliedVersionId,
      rollbackVersionId: input.rollbackVersionId,
      acceptedBy: input.acceptedBy,
      acceptedAt: iso(this.clock),
    }
    this.acceptedChangeSets.set(changeSet.id, changeSet)
    for (const item of items) {
      this.reviewItems.set(item.id, {
        ...item,
        status: 'accepted',
        updatedAt: changeSet.acceptedAt,
      })
    }
    this.persist()
    return changeSet
  }

  rollbackAcceptedChangeSet(changeSetId: string, input: RollbackAcceptedChangeSetInput): AcceptedChangeSet {
    const changeSet = this.acceptedChangeSets.get(changeSetId)
    if (!changeSet) throw new Error(`Unknown AcceptedChangeSet: ${changeSetId}`)
    if (changeSet.rolledBackAt) throw new Error(`AcceptedChangeSet ${changeSetId} is already rolled back.`)
    const next: AcceptedChangeSet = {
      ...changeSet,
      rollbackAppliedVersionId: input.rollbackAppliedVersionId,
      rolledBackBy: input.rolledBackBy,
      rolledBackAt: iso(this.clock),
    }
    this.acceptedChangeSets.set(changeSetId, next)
    this.persist()
    return next
  }

  snapshot(): PreviewReviewSnapshot {
    return {
      version: PREVIEW_REVIEW_SNAPSHOT_VERSION,
      previews: Array.from(this.previews.values()),
      shares: Array.from(this.shares.values()),
      reviewItems: Array.from(this.reviewItems.values()),
      drafts: Array.from(this.drafts.values()),
      acceptedChangeSets: Array.from(this.acceptedChangeSets.values()),
    }
  }

  mergeSnapshot(snapshot: PreviewReviewSnapshot): void {
    if (!isSnapshot(snapshot)) throw new Error('Invalid PreviewReviewSnapshot')
    for (const preview of snapshot.previews) this.previews.set(preview.id, preview)
    for (const share of snapshot.shares) this.shares.set(share.id, share)
    for (const item of snapshot.reviewItems) this.reviewItems.set(item.id, item)
    for (const draft of snapshot.drafts) this.drafts.set(draft.id, draft)
    for (const changeSet of snapshot.acceptedChangeSets) this.acceptedChangeSets.set(changeSet.id, changeSet)
    this.persist()
  }

  replaceSnapshot(snapshot: PreviewReviewSnapshot): void {
    if (!isSnapshot(snapshot)) throw new Error('Invalid PreviewReviewSnapshot')
    this.previews.clear()
    this.shares.clear()
    this.reviewItems.clear()
    this.drafts.clear()
    this.acceptedChangeSets.clear()
    this.mergeSnapshot(snapshot)
  }

  private hydrate(): void {
    const snapshot = this.storage?.load()
    if (!snapshot) return
    this.previews.clear()
    this.shares.clear()
    this.reviewItems.clear()
    this.drafts.clear()
    this.acceptedChangeSets.clear()
    for (const preview of snapshot.previews) this.previews.set(preview.id, preview)
    for (const share of snapshot.shares) this.shares.set(share.id, share)
    for (const item of snapshot.reviewItems) this.reviewItems.set(item.id, item)
    for (const draft of snapshot.drafts) this.drafts.set(draft.id, draft)
    for (const changeSet of snapshot.acceptedChangeSets) this.acceptedChangeSets.set(changeSet.id, changeSet)
  }

  private persist(): void {
    this.storage?.save(this.snapshot())
  }

  private updateReviewItemStatus(reviewItemId: string, status: ReviewItemStatus): ReviewItem {
    const item = this.reviewItems.get(reviewItemId)
    if (!item) throw new Error(`Unknown ReviewItem: ${reviewItemId}`)
    const next = {
      ...item,
      status,
      updatedAt: iso(this.clock),
    }
    this.reviewItems.set(reviewItemId, next)
    this.persist()
    return next
  }
}

export const previewReviewStore = new PreviewReviewStore()
